/* eslint-disable jsx-a11y/no-autofocus */

import React from 'react';
import {
  Item,
  Root,
  Trigger,
  Content,
  Separator,
  Portal,
} from '@radix-ui/react-dropdown-menu';
import pt from 'prop-types';

import { classNames } from 'utils';

import Icon from 'components/icon';
import Field from 'components/form/field';
import { DropdownMenu } from 'components/dropdown-menu';
import { StorefrontEditorSidebarIcon } from 'components/storefront/editor/icon';
import StorefrontEditorIcon from 'components/storefront/editor/icon';

import ModalTemplateNew from './template-new';

const ICON_SIZE = 18;
const DEFAULT_TEMPLATE_ID = '__DEFAULT__';

/** @type {React.MouseEventHandler} */
function stopPropagation(event) {
  event.stopPropagation();
}

/** @type {React.KeyboardEventHandler} */
function onButtonKeyDown(event) {
  switch (event.key) {
    case 'Enter':
    case ' ': {
      const ev = new Event('click', { bubbles: true });
      event.target.dispatchEvent(ev);
      break;
    }

    default:
      break;
  }
}

const contentRest = Object.freeze({
  side: 'bottom',
  sideOffset: 10,
  align: 'start',
});

const subContentRest = Object.freeze({
  side: 'right',
  sideOffset: 15,
  align: 'start',
});

const rootRest = Object.freeze({ modal: false });

export default class TemplateDropdown extends React.PureComponent {
  static propTypes = {
    page: pt.object,
    pageRecord: pt.object,
    altTemplate: pt.string,
    primaryThemeTemplates: pt.object,
    onCreateAlternateTemplate: pt.func,
    onRenameAlternateTemplate: pt.func,
    onDeleteAlternateTemplate: pt.func,
    onChangeAlternateTemplate: pt.func,
  };

  static contextTypes = {
    openModal: pt.func.isRequired,
  };

  constructor(props, context) {
    super(props, context);

    const list = this.makeTemplatesList();

    this.state = {
      templates: list,
      lookup: {
        count: 0,
        results: [],
        loading: false,
        model: '',
        query: { search: '' },
      },
      modal: false,
      defaultTemplate: DEFAULT_TEMPLATE_ID,
      defaultName: '',
      rename: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.primaryThemeTemplates !== this.props.primaryThemeTemplates) {
      this.setState({ templates: this.makeTemplatesList() });
    }
  }

  makeTemplatesList() {
    const { page, primaryThemeTemplates } = this.props;

    return [
      {
        id: DEFAULT_TEMPLATE_ID,
        value: DEFAULT_TEMPLATE_ID,
        label: 'Default template',
      },
      ...(primaryThemeTemplates[page.collection]?.list || []).map(
        (template) => ({
          id: template,
          value: template,
          label: template,
        }),
      ),
    ];
  }

  onSubmitTemplate = (values) => {
    return this.state.rename
      ? this.onRenameTemplate(values, this.state.defaultName)
      : this.onCreateTemplate(values);
  };

  async onCreateTemplate(values) {
    const { page, onCreateAlternateTemplate } = this.props;
    const { name, template } = values;

    const result = await onCreateAlternateTemplate(
      page.collection,
      name,
      template === DEFAULT_TEMPLATE_ID ? '' : template,
    );

    if (result !== false) {
      this.setState({ modal: false });
    }
  }

  async onRenameTemplate(values, oldName) {
    const { page, onRenameAlternateTemplate } = this.props;
    const { name } = values;

    const result = await onRenameAlternateTemplate(
      page.collection,
      name,
      oldName,
    );

    if (result !== false) {
      this.setState({ modal: false });
    }
  }

  onClickDuplicateTemplate = (event) => {
    const template = event.target.dataset.id;

    this.setState({
      modal: true,
      defaultTemplate: template,
      defaultName: template,
      rename: false,
    });
  };

  onClickRenameTemplate = (event) => {
    const template = event.target.dataset.id;

    this.setState({
      modal: true,
      defaultTemplate: template,
      defaultName: template,
      rename: true,
    });
  };

  onClickDeleteTemplate = (event) => {
    const { page, onDeleteAlternateTemplate } = this.props;
    const template = event.target.dataset.id;

    this.context.openModal('ConfirmDelete', {
      title: `${template} template`,
      onConfirm() {
        return onDeleteAlternateTemplate(page.collection, template);
      },
    });
  };

  onChangeTemplate = (_event, template) => {
    if (template === '') {
      return;
    }

    const { onChangeAlternateTemplate } = this.props;

    onChangeAlternateTemplate(
      template.id === DEFAULT_TEMPLATE_ID ? null : template.id,
    );
  };

  onQuery = (search) => {
    search = search.toLowerCase();

    this.setState((state) => {
      const { templates } = state;

      const results = search
        ? templates.filter((template) =>
            template.label.toLowerCase().includes(search),
          )
        : templates;

      return {
        ...state,
        lookup: {
          ...state.lookup,
          count: results.length,
          results,
          query: { search },
        },
      };
    });
  };

  onToggleModal = () => {
    this.setState((state) => ({ ...state, modal: !state.modal }));
  };

  onClickAddTemplate = () => {
    this.setState({
      modal: true,
      defaultTemplate: DEFAULT_TEMPLATE_ID,
      defaultName: '',
      rename: false,
    });
  };

  renderLookupItems = ({ lookup, selected, onClick, onMouseOver }) => {
    const { altTemplate } = this.props;
    const current = altTemplate || DEFAULT_TEMPLATE_ID;

    const lookupItems = lookup.results.map((record) => {
      const itemSelected = selected === record;

      return (
        <button
          key={record.id}
          role="option"
          data-id={record.id}
          aria-selected={itemSelected}
          className={classNames(
            'dropdown-menu-item',
            'storefront-editor--template-dropdown--item',
            {
              selected: itemSelected,
            },
          )}
          onMouseOver={onMouseOver}
          onFocus={onMouseOver}
          onClick={onClick}
          type="button"
        >
          {record.label}

          {record.id === current && (
            <StorefrontEditorIcon
              className="storefront-editor--record-dropdown--icon-check"
              type="check"
              width={ICON_SIZE}
              height={ICON_SIZE}
            />
          )}

          <Root key={record.id} {...rootRest}>
            <Trigger asChild>
              <div
                role="button"
                tabIndex={0}
                className="storefront-editor--template-dropdown--menu-trigger"
                onKeyDown={onButtonKeyDown}
                onClick={stopPropagation}
              >
                <Icon fa="ellipsis-h" />
              </div>
            </Trigger>

            <Portal>
              <Content
                className="dropdown-menu-content storefront-editor--template-dropdown--content"
                {...subContentRest}
                onClick={stopPropagation}
              >
                <Item
                  data-id={record.id}
                  className="storefront-editor--template-dropdown--item"
                  onSelect={this.onClickDuplicateTemplate}
                >
                  <StorefrontEditorIcon
                    className="storefront-editor--template-dropdown--item-icon"
                    type="copy"
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                  />
                  Duplicate
                </Item>

                {record.id !== DEFAULT_TEMPLATE_ID && (
                  <>
                    <Item
                      data-id={record.id}
                      className="storefront-editor--template-dropdown--item"
                      onSelect={this.onClickRenameTemplate}
                    >
                      <StorefrontEditorIcon
                        className="storefront-editor--template-dropdown--item-icon"
                        type="settings"
                        width={ICON_SIZE}
                        height={ICON_SIZE}
                      />
                      Rename
                    </Item>

                    <Separator className="storefront-editor--template-dropdown--separator" />

                    <Item
                      data-id={record.id}
                      className="storefront-editor--template-dropdown--item danger"
                      onSelect={this.onClickDeleteTemplate}
                    >
                      <StorefrontEditorIcon
                        className="storefront-editor--template-dropdown--item-icon"
                        type="trash"
                        width={ICON_SIZE}
                        height={ICON_SIZE}
                      />
                      Delete template
                    </Item>
                  </>
                )}
              </Content>
            </Portal>
          </Root>
        </button>
      );
    });

    const newItem = (
      <button
        key=""
        className="storefront-editor--button-add"
        onClick={this.onClickAddTemplate}
        type="button"
      >
        <StorefrontEditorSidebarIcon type="plus-circle" />
        New template
      </button>
    );

    lookupItems.push(newItem);

    return lookupItems;
  };

  renderTemplateDropdownContent() {
    const { altTemplate } = this.props;
    const current = altTemplate || DEFAULT_TEMPLATE_ID;

    const { lookup } = this.state;

    const value = lookup.results.find((record) => record.id === current);

    return (
      <Field
        type="lookup"
        lookup={lookup}
        placeholder="Search"
        aria-label="Select record template"
        renderLookupItems={this.renderLookupItems}
        onChange={this.onChangeTemplate}
        defaultValue={value}
        onQuery={this.onQuery}
        mountQuery
        queryFocus
        autoFocus
        select
      />
    );
  }

  renderTemplateDropdownTrigger() {
    const { altTemplate } = this.props;

    return (
      <>
        {altTemplate ? altTemplate : 'Default template'}
        <Icon fa="chevron-down" width={ICON_SIZE} height={ICON_SIZE} />
      </>
    );
  }

  render() {
    const { modal, rename, defaultName, defaultTemplate, templates } =
      this.state;

    return (
      <>
        <DropdownMenu
          contentClassName="dropdown-menu-content storefront-editor--record-dropdown--content"
          triggerClassName="button button-sm button-secondary storefront-editor--template-dropdown"
          triggerChildren={this.renderTemplateDropdownTrigger()}
          contentRest={contentRest}
          rootRest={rootRest}
        >
          {this.renderTemplateDropdownContent()}
        </DropdownMenu>

        {modal && (
          <ModalTemplateNew
            rename={rename}
            defaultName={defaultName}
            defaultTemplate={defaultTemplate}
            options={templates}
            onSubmit={this.onSubmitTemplate}
            onClose={this.onToggleModal}
          />
        )}
      </>
    );
  }
}
